import { Button, Checkbox, DatePicker, Form, Input, Spin } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { userRole } from "@/constant";
import ImgUpload from "./ImgUpload";
import { flare } from "@flareapp/js";

const onFinishFailed = (errorInfo) => {
  // eslint-disable-next-line no-console
  console.log("Failed:", errorInfo);
};

const BusinessForm = ({ mode, data }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const whiteLabelChecked = Form.useWatch("whiteLabelAccount", form);
  const { userDetails } = useSelector((state) => state.user);

  const [initialValues, setInitValues] = useState(data);
  const [isLoading, setIsLoading] = useState(false);
  const [businessId, setBusinessId] = useState("");

  const [image, setImage] = useState("");
  const [imageShort, setImageShort] = useState("");
  const [bgImage, setBgImage] = useState("");

  const getLastBusinessId = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get(endpoint.business + "new-id");
      setInitValues({ ...initialValues, id: data });
      setBusinessId(data);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkBusinessUrl = async (_, value) => {
    if (!value) return Promise.reject("Business Url is required");
    // setUrlIsLoading(true);

    if (mode === "edit" && data.businessUrl === value) {
      return Promise.resolve();
    }

    try {
      const response = await api.get(`/business/check-business-url`, {
        params: {
          businessUrl: value,
          unique: true,
        },
      });
      if (!response?.isUnique) {
        return Promise.reject("Business Url is already taken");
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      // setUrlIsLoading(false);
    }

    return Promise.resolve();
  };

  useEffect(() => {
    if (mode === "create") {
      getLastBusinessId();
    }
  }, [mode]);

  useEffect(() => {
    if (mode === "edit") {
      document.title = `${appConfig.name} - Update Business`;
    } else {
      document.title = `${appConfig.name} - Create Business`;
    }
  }, [mode]);

  const handleCreate = async (body) => {
    await api.post(endpoint.business, body);
  };
  const handleUpdate = async (body) => {
    await api.put(endpoint.business + data?._id, body);
  };

  const onFinish = async (value) => {
    setIsLoading(true);
    try {
      const body = {
        ...value,
        customerSince: value?.customerSince ? dayjs(value?.customerSince).format("DD/MM/YYYY") : "",
        whiteLabelAccount: Boolean(value.whiteLabelAccount),
      };
      // return;
      if (mode === "edit") {
        body.logo = data?.logo;
        body.logoShort = data?.logoShort;
        body.bgImage = data?.bgImage;
      }

      image && (body.logo = image);
      imageShort && (body.logoShort = imageShort);
      bgImage && (body.bgImage = bgImage);

      if (!data?.logo && !image) delete body.logo;
      if (!data?.logoShort && !imageShort) delete body.logoShort;
      
      const formData = new FormData();
      for (const key in body) {
        if (["isLimitedHosting", "idleLogoutFot30Min", "multipleDeviceLogin", "whiteLabelAccount"].includes(key)) {
          formData.append(key, !!body[key]);
        } else formData.append(key, body[key] || "");
      }

      if (mode === "edit") {
        await handleUpdate(formData);
      } else {
        await handleCreate(formData);
      }
      await Swal.fire({
        icon: "success",
        text: mode === "edit" ? "Business Successfully Updated" : "Business Successfully Created",
        timer: 1000,
      });
      return navigate("/business");
    } catch (error) {
      flare.report(error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "An error occurred",
        timer: 1000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isSuperAdmin = userDetails?.role === userRole.superAdmin;

  if (isLoading) {
    return <Spin spinning={isLoading} />;
  }
  return (
    <div className="card p-3 m-md-3">
      <div>
        <label className="az-content-label">Manage Businesses</label>
        <span className="d-block py-2">{mode === "edit" ? "Edit Business Profile" : "Create New Business"}</span>
      </div>
      <hr className="mt-0 mb-4" />

      <div className="d-flex justify-content-center">{isLoading && <Spinner animation="border" variant="dark" />}</div>

      <Form
        className="business-form"
        layout="vertical"
        name="business"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        form={form}
        initialValues={{
          ...initialValues,
          defalutDashboard: data?.defalutDashboard?._id || null,
          customerSince: data?.customerSince && data?.customerSince !== "Invalid Date" ? dayjs(data?.customerSince, "DD/MM/YYYY") : "",
        }}
      >
        <Row className="gx-4">
          <h5> Business Information </h5>

          <Col md={6}>
            <Form.Item
              name="name"
              rules={[{ required: true, message: "Please Enter Business Name" }]}
              label="Business Name"
              // style={{ paddingBottom: "0px" }}
            >
              <Input placeholder="Enter Business Name" disabled={!isSuperAdmin} />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="id" rules={[{ required: true, message: "Please Enter Business ID" }]} label="Business ID">
              <Input placeholder="Enter Business ID" value={businessId} disabled />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="companyName" rules={[{ required: true, message: "Please Enter Company Name" }]} label="Company Name">
              <Input placeholder="Enter Company Name" disabled={!isSuperAdmin} />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item label="Company Registration Number" name="registrationNumber">
              <Input placeholder="Enter Company Registration Number" disabled={!isSuperAdmin} />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item rules={[{ required: true, message: "Please Enter Company Address Line 1" }]} label="Company Address Line 1" name="address1">
              <Input placeholder="Enter Company Address Line 1" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item label="Company Address Line 2" name="address2">
              <Input placeholder="Enter Company Address Line 2" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="postcode" label="Postcode">
              <Input placeholder="Enter Postcode" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="city" label="City Name">
              <Input placeholder="Enter City Name" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="state" label="State">
              <Input placeholder="Enter State" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="country" label="Country">
              <Input placeholder="Enter Country" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item name="email" rules={[{ type: "email", message: "Please Provide a Valid Email" }]} label="Email Address">
              <Input placeholder="Enter Email Address" />
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item
              name="phoneNumber"
              label="Phone Number"
              rules={[
                {
                  pattern: /^[0-9]+$/,
                  message: "Please Enter Number Only",
                },
              ]}
            >
              <Input placeholder="Enter Phone Number" />
            </Form.Item>
          </Col>

          {isSuperAdmin && (
            <Col md={6}>
              <Form.Item
                layout="horizontal"
                label="30-minute time-out when idle"
                name="idleLogoutFot30Min"
                className="right-label mb-0"
                labelHeight="24px"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
              <Form.Item
                layout="horizontal"
                label="Multiple active login sessions for single account"
                name="multipleDeviceLogin"
                className="right-label mb-0"
                labelHeight="24px"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
              {/* <Form.Item
                layout="horizontal"
                label="Enable URL login (replaces password)"
                name="urlLogin"
                className="right-label mb-0"
                labelHeight="24px"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item> */}
            </Col>
          )}
        </Row>

        <Row className="gx-4 pt-4">
          <h5> Person In-Charge Contact </h5>
          <Col md={6}>
            <Form.Item name="personName" rules={[{ required: false, message: "Please Enter Name" }]} label="Name">
              <Input placeholder="Enter Name" />
            </Form.Item>
          </Col>

          <Col md={6}>
            <Form.Item name="personTitle" label="Title">
              <Input placeholder="Enter Title" />
            </Form.Item>
          </Col>

          <Col md={6}>
            <Form.Item
              name="personEmail"
              rules={[
                { required: false, message: "Please Enter Email" },
                { type: "email", message: "Please Provide a Valid Email" },
              ]}
              label="Email"
            >
              <Input placeholder="Enter Email" />
            </Form.Item>
          </Col>

          <Col md={6}>
            <Form.Item
              name="personPhone"
              rules={[
                { required: false, message: "Please Enter Phone No" },
                {
                  pattern: /^[0-9]+$/,
                  message: "Please Enter Number Only",
                },
              ]}
              label="Phone Number"
            >
              <Input placeholder="Enter Phone No" />
            </Form.Item>
          </Col>

          <Col md={6}>
            <Form.Item name="personNotes" rules={[{ required: false, message: "Please Enter Notes" }]} label="Notes">
              <Input placeholder="Enter Notes" />
            </Form.Item>
          </Col>
        </Row>

        {userDetails.role === userRole.superAdmin && (
          <Row className="gx-4 pt-4">
            <h5> IVIS Account Information </h5>
            <Col md={6}>
              <Form.Item name="customerSince" label="Customer Since">
                <DatePicker
                  className="w-100"
                  format="DD/MM/YYYY"
                  disabled={!isSuperAdmin}
                  disabledDate={(current) => current && current.valueOf() > new Date()}
                />
              </Form.Item>
            </Col>

            <Col md={6}>
              <Form.Item name="customerType" label="Customer Type">
                <Input placeholder="Enter Customer Type" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>

            <Col md={6}>
              <Form.Item name="systemName" label="System Name">
                <Input placeholder="Enter System Name" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>

            <Col md={6}>
              <Form.Item name="loginPageUrl" label="Login Page URL">
                <Input placeholder="Enter Login Page URL" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>

            <Col md={6}>
              <Form.Item name="accountNotes" label="Notes">
                <Input placeholder="Enter Account Notes" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>
            {/* <Col md={6}>
            <Form.Item name="defalutDashboard" label="Default Dashboard">
              <Select
                placeholder="Select A Default Dashboard"
                allowClear
                showSearch
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              >
                {dashboardList?.length &&
                  dashboardList?.map(({ name, _id }) => <Select.Option value={_id}>{name}</Select.Option>)}
              </Select>
            </Form.Item>
          </Col> */}
            <Col md={6}>
              <Form.Item name="oldivisId" label="Old IVIS ID">
                <Input placeholder="Enter Old IVIS ID" disabled={!isSuperAdmin} />
              </Form.Item>
            </Col>

            {userDetails?.role === userRole.superAdmin && (
              <Col md={6}>
                {/* <Form.Item layout="horizontal" name="isLimitedHosting" valuePropName="checked" label="Limited Hosting (2 Years)">
                <Checkbox></Checkbox>
              </Form.Item> */}

                <Form.Item
                  layout="horizontal"
                  label="Limited Hosting (2 Years)"
                  name="isLimitedHosting"
                  className="right-label mb-0"
                  labelHeight="24px"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
                <Form.Item
                  layout="horizontal"
                  label="White Label Account"
                  name="whiteLabelAccount"
                  className="right-label mb-0"
                  labelHeight="24px"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              </Col>
            )}
          </Row>
        )}

        {whiteLabelChecked && (
          <>
            <Row className="gx-4 pt-4">
              <Col md={6}>
                <Form.Item name="businessUrl" hasFeedback rules={[{ validator: checkBusinessUrl }]} label="Business URL">
                  <Input placeholder="Enter Business URL" />
                </Form.Item>
              </Col>
              <Col md={6}>
                <Form.Item
                  name="mainSiteUrl"
                  label="Login Page Logo URL"
                  rules={[
                    {
                      type: "url",
                      message: "Please Enter a Valid URL",
                    },
                  ]}
                >
                  <Input placeholder="Enter a Valid URL" />
                </Form.Item>
              </Col>
            </Row>

            <Row className="gx-4 pt-4">
              <Col md={6}>
                <Form.Item initialValue={image} name="logo" label="Company Logo" extra="Recommended aspect ratio: 16:9 (e.g. 1280×720px)">
                  <ImgUpload url={data?.logo} image={image} setImage={setImage} />
                </Form.Item>
              </Col>
              <Col md={6}>
                <Form.Item
                  initialValue={imageShort}
                  name="logoShort"
                  label="Company Logo (Short Version)"
                  extra="Recommended aspect ratio: 1:1 (e.g. 1280×1280px)"
                >
                  <ImgUpload url={data?.logoShort} image={imageShort} setImage={setImageShort} />
                </Form.Item>
              </Col>
              <Col md={6}>
                <Form.Item initialValue={bgImage} name="bgImage" label="Background Image">
                  <ImgUpload url={data?.bgImage} image={bgImage} setImage={setBgImage} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
          <Button type="primary" htmlType="submit">
            {mode === "edit" ? "Update Business" : "Create New Business"}
          </Button>
          <Button type="primary" className="bg-secondary" onClick={() => navigate(-1)} htmlType="button">
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BusinessForm;

BusinessForm.propTypes = {
  mode: PropTypes.string,
  data: PropTypes.object,
};
